import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Download,
  Loader2,
  FileSpreadsheet,
  Users,
  DollarSign,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import ApiService from "@/services/api";
import { getEndpointUrl } from "@/config/api";
import {
  ExportFile,
  ExportType,
  ExportFormat,
  ListExportsResponse,
} from "@/types/payroll-validator";

interface ExportsCardProps {
  sessionId: string;
}

const exportConfig: Record<
  ExportType,
  {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
  }
> = {
  earnings: {
    title: "Earnings Configuration",
    description: "All earnings types with tax/NIC treatment",
    icon: <DollarSign className="h-5 w-5" />,
    color: "text-green-600 bg-green-50 border-green-200",
  },
  deductions: {
    title: "Deductions Configuration",
    description: "All deduction types with pre/post tax treatment",
    icon: <FileSpreadsheet className="h-5 w-5" />,
    color: "text-orange-600 bg-orange-50 border-orange-200",
  },
  pay_register: {
    title: "Pay Register",
    description: "Complete payroll data for all employees",
    icon: <Users className="h-5 w-5" />,
    color: "text-blue-600 bg-blue-50 border-blue-200",
  },
  pensions: {
    title: "Pension Breakdown",
    description: "Pension scheme details and contributions",
    icon: <FileText className="h-5 w-5" />,
    color: "text-purple-600 bg-purple-50 border-purple-200",
  },
};

export const ExportsCard: React.FC<ExportsCardProps> = ({ sessionId }) => {
  const [exports, setExports] = useState<ExportFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    fetchExports();

    // Poll for exports while any are still generating
    const interval = setInterval(() => {
      const hasGenerating = exports.some((e) => e.status === "generating");
      if (hasGenerating) {
        fetchExports();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [sessionId]);

  const fetchExports = async () => {
    try {
      const data = await ApiService.request<ListExportsResponse>(
        "listSessionExports",
        {
          urlParams: { sessionId },
          skipAuth: true,
        },
      );
      setExports(data.exports);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch exports:", err);
      setError(err instanceof Error ? err.message : "Failed to load exports");
      setLoading(false);
    }
  };

  const handleDownload = async (
    exportType: ExportType,
    format: ExportFormat,
  ) => {
    const downloadKey = `${exportType}-${format}`;
    setDownloading(downloadKey);

    try {
      // Map export type to endpoint key
      const endpointMap: Record<ExportType, string> = {
        earnings: "downloadEarningsExport",
        deductions: "downloadDeductionsExport",
        pay_register: "downloadPayRegisterExport",
        pensions: "downloadPensionsExport",
      };

      const endpoint = endpointMap[exportType];

      // Create URL with format query parameter
      const baseUrl = getEndpointUrl(endpoint, { sessionId });
      const downloadUrl =
        "csv" === format ? baseUrl : `${baseUrl}?format=${format}`;

      // Create a temporary link and click it to trigger download
      // This avoids popup blockers
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to download export. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const getExportsByType = (type: ExportType) => {
    return exports.filter((e) => e.export_type === type);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + " " + sizes[i];
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="default" className="bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3 mr-1" />
            Ready
          </Badge>
        );
      case "generating":
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-700">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Generating
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Exports</CardTitle>
          <CardDescription>Loading exports...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Exports</CardTitle>
          <CardDescription>Unable to load exports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (exports.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Exports</CardTitle>
          <CardDescription>Exports are being generated...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            This usually takes a few seconds
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Download Exports</CardTitle>
        <CardDescription>
          Export your payroll data for third-party providers
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(Object.keys(exportConfig) as ExportType[]).map((exportType) => {
            const config = exportConfig[exportType];
            const typeExports = getExportsByType(exportType);
            const csvExport = typeExports.find((e) => e.file_format === "csv");
            const jsonExport = typeExports.find(
              (e) => e.file_format === "json",
            );

            return (
              <Card key={exportType} className={`border ${config.color}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex-shrink-0 ${config.color} p-2 rounded-lg`}
                    >
                      {config.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {config.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {config.description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {/* CSV Download */}
                        {csvExport && (
                          <div className="flex items-center gap-2">
                            {getStatusBadge(csvExport.status)}
                            {csvExport.status === "completed" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleDownload(exportType, "csv")
                                  }
                                  disabled={downloading === `${exportType}-csv`}
                                >
                                  {downloading === `${exportType}-csv` ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : (
                                    <Download className="h-3 w-3 mr-1" />
                                  )}
                                  CSV
                                </Button>
                                <span className="text-xs text-gray-500">
                                  {formatFileSize(csvExport.file_size)}
                                </span>
                              </>
                            )}
                          </div>
                        )}

                        {/* JSON Download */}
                        {jsonExport && (
                          <div className="flex items-center gap-2">
                            {csvExport
                              ? null
                              : getStatusBadge(jsonExport.status)}
                            {jsonExport.status === "completed" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleDownload(exportType, "json")
                                  }
                                  disabled={
                                    downloading === `${exportType}-json`
                                  }
                                >
                                  {downloading === `${exportType}-json` ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : (
                                    <Download className="h-3 w-3 mr-1" />
                                  )}
                                  JSON
                                </Button>
                                <span className="text-xs text-gray-500">
                                  {formatFileSize(jsonExport.file_size)}
                                </span>
                              </>
                            )}
                          </div>
                        )}

                        {/* Show error if failed */}
                        {(csvExport?.status === "failed" ||
                          jsonExport?.status === "failed") && (
                          <div className="text-xs text-red-600 mt-1">
                            Export generation failed
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">About these exports</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Exports are automatically generated after validation</li>
                <li>Choose CSV for spreadsheets or JSON for API integration</li>
                <li>All amounts and calculations are included</li>
                <li>Exports remain available for 30 days</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
